import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { NsofarLogo } from './NsofarLogo';

let deferredPrompt = null;

export function InstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Check if dismissed recently (24h)
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 86400000) return;

    // iOS detection (no beforeinstallprompt)
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(isiOS);
    if (isiOS) {
      const isInSafari = /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua);
      if (isInSafari) setShowPrompt(true);
      return;
    }

    // Android / Desktop — listen for install prompt
    const handler = (e) => {
      e.preventDefault();
      deferredPrompt = e;
      setShowPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setShowPrompt(false);
      deferredPrompt = null;
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', String(Date.now()));
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-[380px] z-[9998] animate-fade-in-up"
      data-testid="pwa-install-prompt">
      <div className="rounded-2xl p-4 shadow-xl border"
        style={{ background: 'var(--surface)', borderColor: 'var(--border-color)', boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 flex items-center justify-center overflow-hidden"
            style={{ width: 56, height: 56, borderRadius: 12, background: 'var(--surface)', border: '1px solid var(--border-color)' }}>
            <NsofarLogo size="xs" className="scale-75" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold mb-0.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-main)' }}>
              Install nsofar
            </h3>
            {isIOS ? (
              <p className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                Tap the <strong>Share</strong> button in Safari, then select <strong>"Add to Home Screen"</strong> for the best experience.
              </p>
            ) : (
              <p className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                Add nsofar to your home screen for quick access, offline support, and a native app experience.
              </p>
            )}
          </div>
          <button onClick={handleDismiss} className="p-1 rounded-lg flex-shrink-0 hover:bg-[var(--hover-surface)] transition-colors"
            data-testid="pwa-install-dismiss">
            <X size={16} style={{ color: 'var(--text-light)' }} />
          </button>
        </div>
        {!isIOS && (
          <div className="flex gap-2 mt-3">
            <button onClick={handleInstall}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:shadow-md"
              style={{ background: 'var(--brand-primary)' }}
              data-testid="pwa-install-button">
              Install App
            </button>
            <button onClick={handleDismiss}
              className="px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
              style={{ color: 'var(--text-muted)', background: 'var(--bg-main)' }}
              data-testid="pwa-install-later">
              Later
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
